import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  Card,
  CardContent,
  useTheme,
  alpha
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useCurrency } from '../../contexts/CurrencyContext';
import CurrencyDisplay from './CurrencyDisplay';

const CurrencyTestPage: React.FC = () => {
  const theme = useTheme();
  const { 
    formatCurrency, 
    formatCurrencyWithCommas, 
    formatAmount, 
    formatAmountWithCommas,
    refreshCurrency,
    loading
  } = useCurrency();
  
  const [testAmount, setTestAmount] = useState<number>(1234.56);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshCurrency();
    } catch (error) {
      console.error('Error refreshing currency:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const testAmounts = [
    0,
    1,
    10.5,
    100,
    1234.56,
    10000,
    123456.789,
    1000000
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          اختبار نظام العملة
        </Typography>
        <Typography variant="body1" color="text.secondary">
          اختبار تنسيق العملة والتأكد من عمل النظام بشكل صحيح
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Currency Display */}
        <Grid item xs={12} md={6}>
          <CurrencyDisplay showExample={true} exampleAmount={testAmount} />
        </Grid>

        {/* Test Controls */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <SettingsIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                أدوات الاختبار
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="مبلغ الاختبار"
              type="number"
              value={testAmount}
              onChange={(e) => setTestAmount(parseFloat(e.target.value) || 0)}
              sx={{ mb: 2 }}
              inputProps={{
                step: 0.01,
                min: 0
              }}
            />

            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing || loading}
              sx={{ mb: 2 }}
            >
              {refreshing ? 'جاري التحديث...' : 'تحديث إعدادات العملة'}
            </Button>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                💡 <strong>نصيحة:</strong> جرب تغيير العملة من صفحة الإعدادات العامة ثم اضغط "تحديث إعدادات العملة" لرؤية التغيير
              </Typography>
            </Alert>
          </Paper>
        </Grid>

        {/* Test Results */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              نتائج اختبار التنسيق
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              اختبار تنسيق مبالغ مختلفة بالعملة الحالية
            </Typography>

            <Grid container spacing={2}>
              {testAmounts.map((amount, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      '&:hover': {
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        المبلغ الأصلي: {amount}
                      </Typography>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            مع العملة:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(amount)}
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            مع فواصل:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrencyWithCommas(amount)}
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            بدون عملة:
                          </Typography>
                          <Typography variant="body2">
                            {formatAmount(amount)}
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            بدون عملة + فواصل:
                          </Typography>
                          <Typography variant="body2">
                            {formatAmountWithCommas(amount)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Usage Examples */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              أمثلة على الاستخدام
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              كيفية استخدام نظام العملة في المكونات
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.5),
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflow: 'auto'
              }}
            >
              <pre>{`// في أي مكون React
import { useCurrency } from '../../contexts/CurrencyContext';

const MyComponent = () => {
  const { formatCurrency, formatCurrencyWithCommas } = useCurrency();
  
  return (
    <div>
      <p>السعر: {formatCurrency(100.50)}</p>
      <p>المجموع: {formatCurrencyWithCommas(1234567.89)}</p>
    </div>
  );
};`}</pre>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CurrencyTestPage;
